import React, { useState, useEffect, useCallback } from 'react';
import { Table, Tag, Input, Card, Statistic, Button, Tooltip, Modal, message } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserSlash, faRotate, faCalendarWeek, faCalendarDays } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { SERVER_URL } from '../../../databucket';
import dayjs from 'dayjs';

const UnsubscribeHistory = () => {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await axios.get(`${SERVER_URL}/api/ses/unsubscribe-history`, {
        params: { page, pageSize: 50, search: search || undefined },
      });
      setData(res.rows || []);
      setTotal(res.total || 0);
      setStats(res.stats || {});
    } catch { /* ignore */ }
    setLoading(false);
  }, [page, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSearch = (val) => { setSearch(val); setPage(1); };

  const handleResubscribe = async (email) => {
    Modal.confirm({
      title: 'Re-subscribe contact?',
      content: `This will re-subscribe ${email} and allow sending emails to them again.`,
      okText: 'Yes, re-subscribe',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const { data: res } = await axios.post(`${SERVER_URL}/api/ses/resubscribe`, { email });
          if (res?.success) {
            message.success(res.message || 'Re-subscribed successfully');
            fetchData();
          } else {
            message.error('Failed to re-subscribe');
          }
        } catch {
          message.error('Failed to re-subscribe');
        }
      },
    });
  };

  const columns = [
    {
      title: 'Email', dataIndex: 'contactEmail', key: 'contactEmail', width: 220,
      render: (text) => <span style={{ fontWeight: 500 }}>{text}</span>,
    },
    {
      title: 'Contact Name', dataIndex: 'contactName', key: 'contactName', width: 160,
      render: (text) => text || '—',
    },
    {
      title: 'Agency', dataIndex: 'agencyName', key: 'agencyName', width: 180,
      render: (text) => text || '—',
    },
    {
      title: 'Lead ID', dataIndex: 'leadID', key: 'leadID', width: 80,
      render: (text) => text ? <span style={{ fontWeight: 600, color: '#3b82f6' }}>{text}</span> : '—',
    },
    {
      title: 'Unsubscribed At', dataIndex: 'unsubscribedAt', key: 'unsubscribedAt', width: 180,
      render: (text) => text ? (
        <span style={{ fontSize: 12, color: '#64748b' }}>{dayjs(text).format('DD MMM YYYY, hh:mm A')}</span>
      ) : '—',
      sorter: (a, b) => new Date(a.unsubscribedAt) - new Date(b.unsubscribedAt),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Sent By', dataIndex: 'unsubscribedSentBy', key: 'unsubscribedSentBy', width: 140,
      render: (text) => text || '—',
    },
    {
      title: 'From Email', dataIndex: 'unsubscribedFromEmail', key: 'unsubscribedFromEmail', width: 200,
      render: (text) => text ? (
        <Tag color="blue" style={{ borderRadius: 10, fontSize: 10 }}>{text}</Tag>
      ) : '—',
    },
    {
      title: 'Action', key: 'action', width: 120, align: 'center',
      render: (_, record) => (
        <Button
          size="small"
          type="primary"
          ghost
          onClick={() => handleResubscribe(record.contactEmail)}
          style={{ borderRadius: 8, fontSize: 11, fontWeight: 600 }}
        >
          Re-subscribe
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: '20px 24px' }}>
      <h2 style={{ marginBottom: 16, fontWeight: 700, fontSize: 20 }}>
        <FontAwesomeIcon icon={faUserSlash} style={{ marginRight: 8, color: '#ef4444' }} />
        Unsubscribe History
      </h2>

      {/* Stats Cards */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <Card size="small" style={{ borderRadius: 12, border: '1.5px solid #fca5a5', flex: '1 1 0', minWidth: 140 }}>
          <Statistic
            title="Total Unsubscribed"
            value={stats.total || 0}
            valueStyle={{ color: '#ef4444', fontWeight: 700 }}
            prefix={<FontAwesomeIcon icon={faUserSlash} />}
          />
        </Card>
        <Card size="small" style={{ borderRadius: 12, border: '1.5px solid #fcd34d', flex: '1 1 0', minWidth: 140 }}>
          <Statistic
            title="This Week"
            value={stats.thisWeek || 0}
            valueStyle={{ color: '#f59e0b', fontWeight: 700 }}
            prefix={<FontAwesomeIcon icon={faCalendarWeek} />}
          />
        </Card>
        <Card size="small" style={{ borderRadius: 12, border: '1.5px solid #93c5fd', flex: '1 1 0', minWidth: 140 }}>
          <Statistic
            title="This Month"
            value={stats.thisMonth || 0}
            valueStyle={{ color: '#3b82f6', fontWeight: 700 }}
            prefix={<FontAwesomeIcon icon={faCalendarDays} />}
          />
        </Card>
      </div>

      {/* Search + Refresh */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
        <Input.Search
          placeholder="Search by email, contact, or agency..."
          allowClear
          onSearch={handleSearch}
          style={{ maxWidth: 400 }}
        />
        <Button icon={<FontAwesomeIcon icon={faRotate} />} onClick={fetchData}>Refresh</Button>
      </div>

      {/* Table */}
      <Table
        columns={columns}
        dataSource={data}
        rowKey={(r, i) => `${r.leadId}-${r.contactEmail}-${i}`}
        loading={loading}
        pagination={{
          current: page, total, pageSize: 50,
          onChange: (p) => setPage(p),
          showTotal: (t) => `${t} unsubscribed contacts`,
          size: 'small',
        }}
        size="small"
        scroll={{ x: 1100 }}
        style={{ borderRadius: 12, overflow: 'hidden' }}
      />
    </div>
  );
};

export default UnsubscribeHistory;
